import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  Icon,
} from '@chakra-ui/react';
import PhysicianPartnershipForm from '../components/forms/PhysicianPartnershipForm';
import { FaWalking, FaSpa, FaClipboardCheck } from 'react-icons/fa';
import Roster from '../components/team/Roster';
import boardMembers from '../components/team/boardMembers';
import { Reveal, RevealGroup, RevealItem } from '../components/motion';
import { PageHero, SectionHeading, LightSheet, InkBand } from '../components/premium';

const AboutTeam = () => {
  const leadershipTeam = [
    {
      name: 'Ray Brown',
      title: 'Chief Executive Officer',
      image: '/team/portraits/ray-brown.jpg',
      bio: "With over 25 years of leadership with a patient-centric focus, Ray is dedicated to advancing healthcare innovation with emphasis on patient experience and kindness as Chief Executive Officer at Lake Lucien Surgery Center. His mission is to foster a patient- and physician-centric environment where innovative practices enhance satisfaction and optimize reported outcomes. Driven by a commitment to collaboration and continuous improvement, Ray works to align organizational goals with the evolving needs of patients, bringing diverse perspectives to elevate care standards. At Lake Lucien Surgery Center, by prioritizing patient experience, Ray and his team deliver measurable improvements in care quality and build trust within the community, positioning the center as a leader in healthcare innovation."
    },
    {
      name: 'Omar Beidas',
      title: 'Chief Medical Officer',
      image: '/team/portraits/omar-beidas.jpg',
      bio: "Renowned for his expertise in body contouring and plastic surgery, Dr. Omar Beidas is trusted worldwide for both his exceptional results and the confidence he instills in patients. Every patient's journey is guided by rigorous safety protocols and ethical practice, ensuring their well-being comes before all else. As chief medical officer, he is committed to evidence-based medicine which means only proven, reliable techniques are used at each stage of care. The center will provide compassionate and deeply personalized experiences, respecting each individual's goals, anxieties, and preferences. Patients are listened to and cared for with sensitivity, creating a private and comfortable environment throughout their journey. Dr. Omar Beidas is an active member of leading global societies of plastic and reconstructive surgery, ensuring adherence to the highest standards of patient safety and care."
    },
    {
      name: 'Meri Brown',
      title: 'Vice President of Kindness',
      image: '/team/portraits/meri-brown.jpg',
      bio: "As Vice President of Kindness, Meri Brown brings more than 30 years of dedicated one-on-one patient care, with over 100,000 patient-facing interactions rooted in compassion, trust, and warmth. She is highly experienced in supporting patients emotionally through both pre-operative and post-operative journeys, offering reassurance during vulnerable moments. Meri also brings deep expertise in inflammation management and custom compression through her work designing medical compression garments with FormRx. This experience enables her to train and guide case managers in delivering empathetic, patient-centered care. At Lucien ASC, Meri is committed to ensuring the patient's experience is best-in-class—defined by kindness, connection, and excellence."
    },
    {
      name: 'Michael Hammons',
      title: 'Director of Clinical Excellence',
      image: '/team/portraits/mike-h.jpg',
      bio: "As Director of Clinical Excellence, Michael Hammons is a Certified Surgical Technologist (CST) with over 20 years of experience across a broad range of surgical specialties, including Orthopedics, Trauma, Plastic Surgery, Women's Health, and General Surgery. He is highly skilled in advanced robotic-assisted procedures, with more than a decade of hands-on experience using systems such as DaVinci and Mako robotics. Beyond the operating room, Michael brings proven leadership and operational expertise, having successfully managed surgical materials and resources to support efficiency and optimal patient outcomes. Passionate about education and mentorship, he is dedicated to training and guiding surgical technologists and healthcare professionals, ensuring the highest standards of clinical excellence at Lucien ASC."
    }
  ];

  // The Anesthesia Department's ERAS points, in the department's own words.
  const eras = [
    {
      icon: FaWalking,
      lead: 'Faster Healing',
      text: 'Proven protocols that help you go home sooner and return to your daily routine.',
    },
    {
      icon: FaSpa,
      lead: 'Comfort Without "Fog"',
      text: 'We use "multimodal" pain management—a combination of targeted numbing and non-opioid medications—to keep you comfortable while minimizing side effects like nausea or grogginess.',
    },
    {
      icon: FaClipboardCheck,
      lead: 'Proactive Care',
      text: 'We work with you before your surgery to ensure your body is strong, hydrated, and ready for a successful recovery.',
    },
  ];

  const operationsTeam = [
    {
      name: 'Ibrahim Sunder',
      title: 'Financial Analyst',
      image: '/team/portraits/ibrahim-sunder.jpg',
      bio: "Ibrahim applies advanced financial methodologies to help guide decisions, prevent financial risks, and strengthen systems that improve outcomes for patients and physicians. He holds a Master of Science in Finance from the University of Wisconsin–Milwaukee and a Bachelor's in International Banking & Finance. In previous roles across commercial banking, investment analysis, and logistics finance, he reduced credit risk, built strategic financial models, and improved operational efficiency for multi-sector clients. At the ASC, Ibrahim supports financial operations while also assisting in building management systems that uphold the highest standards of safety, efficiency, and quality of care."
    },
    {
      name: 'Priyasha',
      title: 'Accountant',
      image: '/team/portraits/priyasha.jpg',
      bio: "Priyasha designs and maintains the financial systems that keep the ASC running with discipline and transparency. Her work ensures that leadership decisions are grounded in reliable data and executed with precision. She holds a Master of Science in Financial Analysis from the University of Wisconsin–Milwaukee and a Bachelor's in Banking & Finance. In previous roles across banking, financial modeling, and business operations, she guided commercial clients on financing, improved forecasting accuracy, optimized budgets, and led initiatives that strengthened both efficiency and engagement. Priyasha aims in ensures the ASC's financial operations are managed with the same rigor and reliability expected in patient care - maintaining accuracy, compliance, and trust at the highest level."
    },
    {
      name: 'Ignacio Nunez',
      title: 'IT Manager',
      image: '/team/portraits/ignacio-nunez.jpg',
      bio: "Ignacio leads technology and data architecture for Lake Lucien Surgery Center, owning the systems a busy surgical center depends on: the clinical and operational data infrastructure, security and HIPAA compliance, systems integration, and the reliability clinical teams count on every day. He brings more than 15 years of full-stack engineering experience across modern web frameworks, cloud infrastructure and DevOps, and database design, giving him hands-on depth across the entire stack rather than oversight alone. At Lake Lucien he is responsible for all technology systems, from the patient portal and website to internal management and reporting tools, ensuring secure, well-integrated data flows and a digital experience that reflects the center's standards for precision and patient care."
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      {/* Photographic hero, shared with every inner page. See
          components/premium/PageHero for why the photo is no longer
          hidden under a cyan gradient. */}
      <PageHero
        title={"Meet Our Team"}
        subtitle={"Dedicated professionals committed to excellence in surgical care and patient experience"}
        image="/facility/exterior.jpg"
        imagePosition="left center"
      />

      {/* Leadership Team Section */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={0} align="stretch">
          <SectionHeading
            as="h2"
            size="xl"
            title={<Box as="span" letterSpacing="0.02em">LEADERSHIP TEAM</Box>}
          />

          {/* Four across: the leadership team reads as one row of equals. */}
          <Roster
            people={leadershipTeam}
            columns={{ base: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
          />
        </VStack>
      </Container>

      {/* Board of Directors. Shares the page with the leadership team rather
          than living on its own route, so governance and management read as
          one organization. /board redirects here, to this anchor. */}
      <Box id="board" bg="mist.50" scrollMarginTop="150px">
        <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
          <VStack spacing={0} align="stretch">
            <SectionHeading
              as="h2"
              size="xl"
              title={<Box as="span" letterSpacing="0.02em">BOARD OF DIRECTORS</Box>}
              lede="Experienced physician and healthcare leaders guiding the governance and clinical vision of Lake Lucien Surgery Center"
            />

            <Box maxW="1180px" mx="auto" w="100%">
              <Roster
                people={boardMembers}
                columns={{ base: 'minmax(0, 420px)', lg: 'repeat(3, 1fr)' }}
                gap={{ base: 6, md: 6, xl: 8 }}
              />
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Anesthesia. Not a person, so not a portrait card: the department's
          ERAS approach as its own dark band. Wording is the department's own,
          only broken into its headings and points. */}
      <InkBand image="/facility/operating-room.jpg" imageOpacity={0.14}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1.1fr' }} gap={{ base: 12, lg: 20 }} alignItems="start">
            <Reveal>
              <HStack spacing={2.5} mb={5}>
                <Box w="6px" h="6px" borderRadius="full" bg="starOfLife.300" />
                <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="0.18em" color="whiteAlpha.800">
                  Anesthesia Department
                </Text>
              </HStack>
              <Heading as="h2" fontSize={{ base: '2xl', md: '2.4rem' }} fontWeight="600" letterSpacing="-0.025em" lineHeight="1.15" color="white">
                Our Commitment to Your Recovery
              </Heading>
              <Text mt={6} fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.75" color="whiteAlpha.800">
                We believe your surgical journey shouldn't just be about the procedure—it should be about how quickly and comfortably you return to your normal life. Our anesthesia team uses a specialized approach called ERAS (Enhanced Recovery After Surgery) to put you at the center of your care.
              </Text>
              <Box mt={10} pt={8} borderTop="1px solid" borderColor="whiteAlpha.200">
                <Heading as="h3" fontSize="lg" fontWeight="600" color="white" mb={3}>
                  Your Role on the Team
                </Heading>
                <Text lineHeight="1.75" color="whiteAlpha.800">
                  You are the most important member of our care team. We will guide you through simple steps—like special pre-surgery drinks for energy and getting you moving shortly after your procedure—to ensure the best possible outcome.
                </Text>
              </Box>
            </Reveal>

            <Box>
              <Reveal>
                <Heading as="h3" fontSize="lg" fontWeight="600" color="white" mb={3}>
                  Why ERAS Matters for You
                </Heading>
                <Text lineHeight="1.75" color="whiteAlpha.800" mb={7}>
                  By using the latest medical science, we aim to reduce the physical and emotional stress surgery puts on your body. Our practice focuses on:
                </Text>
              </Reveal>
              <RevealGroup>
                <VStack spacing={4} align="stretch">
                  {eras.map((point) => (
                    <RevealItem key={point.lead}>
                      <HStack
                        align="flex-start"
                        spacing={5}
                        p={{ base: 5, md: 6 }}
                        borderRadius="20px"
                        bg="rgba(255,255,255,0.06)"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.14)"
                      >
                        <Box
                          flexShrink={0}
                          w="44px"
                          h="44px"
                          borderRadius="full"
                          display="grid"
                          placeItems="center"
                          bg="rgba(255,255,255,0.06)"
                          border="1px solid"
                          borderColor="rgba(255,255,255,0.16)"
                        >
                          <Icon as={point.icon} boxSize={4} color="starOfLife.300" />
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="white" mb={1}>
                            {point.lead}
                          </Text>
                          <Text color="whiteAlpha.800" lineHeight="1.7" fontSize="sm">
                            {point.text}
                          </Text>
                        </Box>
                      </HStack>
                    </RevealItem>
                  ))}
                </VStack>
              </RevealGroup>
            </Box>
          </Grid>
        </Container>
      </InkBand>

      {/* Business Operations Team and Physician Partnership, together on one
          quiet mist sheet that slides up over the anesthesia band. */}
      <LightSheet tone="mist">
        <Container maxW="container.xl">
          <VStack spacing={0} align="stretch">
            <SectionHeading
              as="h2"
              size="xl"
              title={<Box as="span" letterSpacing="0.02em">BUSINESS OPERATIONS TEAM</Box>}
            />

            <Roster people={operationsTeam} />
          </VStack>
        </Container>

        {/* Physician Partnership Form Section */}
        <Container maxW="container.xl" pt={{ base: 16, md: 24 }}>
          <Reveal>
            <PhysicianPartnershipForm />
          </Reveal>
        </Container>
      </LightSheet>

      {/* CTA Section */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <Reveal>
          <Box
            bg="brand.900"
            borderRadius={{ base: '24px', md: '32px' }}
            px={{ base: 6, md: 12 }}
            py={{ base: 12, md: 16 }}
            textAlign="center"
            position="relative"
            overflow="hidden"
          >
            <VStack spacing={6} position="relative" zIndex={1}>
              <Box w="40px" h="2px" bg="starOfLife.400" />
              <Heading size="xl" color="white" fontWeight="600">
                Join Our Team
              </Heading>
              <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl" lineHeight="1.7">
                We're always looking for talented professionals who share our commitment to exceptional patient care
              </Text>
              <HStack spacing={4} pt={4}>
                <Box
                  as="a"
                  href="/contact"
                  px={8}
                  py={4}
                  bg="white"
                  color="brand.900"
                  borderRadius="full"
                  fontWeight="600"
                  fontSize="lg"
                  cursor="pointer"
                  boxShadow="0 10px 30px -14px rgba(0,0,0,0.55)"
                  transition="transform 0.35s cubic-bezier(0.16,1,0.3,1)"
                  _hover={{
                    transform: 'translateY(-2px)',
                  }}
                >
                  Contact Us
                </Box>
              </HStack>
            </VStack>
          </Box>
        </Reveal>
      </Container>
    </Box>
  );
};

export default AboutTeam;
